// subPlotMark.ts - the outline and wash drawn on the cell a click picked.
//
// The whole-file plot mesh carries its own selection inside its shader and is
// drawn only in the walkaround, so above the SURFACE edge - where the leaves
// are the terrain - a picked cell moved the panel and left the ground
// untouched. This is the mark for those bands, built in the body-fixed metres
// the leaves are built in (`src/atlas/tileMesh.ts`) so it sits on the same
// ground with no frame of its own to disagree about.

import * as THREE from 'three';

/** Points laid along each of the six edges. */
const EDGE_STEPS = 12;
const BOUNDARY = EDGE_STEPS * 6;
/**
 * Rings out from the centre, as fractions of the hexagon.
 *
 * The last one is OUTSIDE the boundary: the rim is a band centred on the edge,
 * and a mesh that stopped at the edge would draw half of it.
 */
const RINGS = [0, 0.62, 0.9, 1, 1.14];
const SQRT3 = Math.sqrt(3);

/** The six corners of a pointy-along-Z hexagon of unit circumradius. */
const CORNERS: ReadonlyArray<readonly [number, number]> = [
  [0, -1], [SQRT3 / 2, -0.5], [SQRT3 / 2, 0.5], [0, 1], [-SQRT3 / 2, 0.5], [-SQRT3 / 2, -0.5],
];

/** The boundary walked once round, as unit offsets from the centre. */
function boundary(): Float32Array {
  const out = new Float32Array(BOUNDARY * 2);
  let k = 0;
  for (let e = 0; e < 6; e++) {
    const a = CORNERS[e]!;
    const b = CORNERS[(e + 1) % 6]!;
    for (let i = 0; i < EDGE_STEPS; i++) {
      const t = i / EDGE_STEPS;
      out[k++] = a[0] + (b[0] - a[0]) * t;
      out[k++] = a[1] + (b[1] - a[1]) * t;
    }
  }
  return out;
}

const VERT = `
  attribute float aRing;
  varying float vRing;
  void main() {
    vRing = aRing;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAG = `
  precision highp float;
  uniform vec3 uColor;
  uniform float uGain, uTime, uPulse, uFill;
  varying float vRing;
  void main() {
    // A band centred on the boundary, so the outline reads at any zoom where
    // the edge itself is under a pixel wide, and a wash inside it.
    float d = (vRing - 1.0) / 0.10;
    float rim = exp(-d * d);
    float fill = 1.0 - smoothstep(0.96, 1.02, vRing);
    // Never let the pulse reach zero - a mark that periodically vanishes reads
    // as a rendering fault rather than as emphasis.
    float pulse = mix(1.0, 0.78 + 0.22 * sin(uTime * 3.0), uPulse);
    float a = (fill * 0.22 * uFill + rim * 0.85) * uGain * pulse;
    gl_FragColor = vec4(uColor * (0.55 + 0.45 * rim), a);
  }
`;

/**
 * How much of the mark is drawn.
 *
 * The wash is what makes the cell obvious and also what hides the ground under
 * it, and the rim is what a player reads a boundary off and also what covers
 * whatever the boundary runs through, so which of the two is worth its cost is
 * the player's call and not ours.
 */
export type SubPlotMarkMode = 'fill' | 'edge' | 'off';

export interface SubPlotMarkCell {
  id: string;
  /** Centre in region-local metres, the frame `plotField` lays its cells in. */
  x: number;
  z: number;
  radius_m: number;
}

export interface SubPlotMarkConfig {
  /**
   * The drawn ground at a point of region-local metres, body-fixed, or null
   * where the caller has no field to answer from.
   */
  groundAt(xM: number, zM: number, out: THREE.Vector3): THREE.Vector3 | null;
  /** Linear rgb of the rim; the wash is the same colour, darker. */
  colour: readonly [number, number, number];
  /** How hard it is drawn, 0 to 1. */
  gain: number;
  /** Whether the rim breathes. The selection does; the cursor's answer does not. */
  pulse: boolean;
}

export interface SubPlotMarkHandle {
  readonly object: THREE.Object3D;
  /** Which cell is marked, or null. */
  readonly cellId: string | null;
  /** Whether the mark is on the ground right now. */
  readonly drawn: boolean;
  /** Mark this cell, or take the mark off. True when what is drawn changed. */
  set(cell: SubPlotMarkCell | null): boolean;
  /** Lay the vertices on the ground again, after it moved under them. */
  refresh(): void;
  /** Whether the mark is in the draw at all, which the band decides. */
  setVisible(on: boolean): void;
  /** How much of it is drawn, which the player decides. */
  setMode(mode: SubPlotMarkMode): void;
  /** Advance the pulse by a frame. */
  update(dtSeconds: number): void;
  dispose(): void;
}

export function createSubPlotMark(config: SubPlotMarkConfig): SubPlotMarkHandle {
  const unit = boundary();
  const rings = RINGS.length;
  const count = rings * BOUNDARY;
  const positions = new Float32Array(count * 3);
  const ringAttr = new Float32Array(count);
  for (let r = 0; r < rings; r++) {
    for (let i = 0; i < BOUNDARY; i++) ringAttr[r * BOUNDARY + i] = RINGS[r]!;
  }
  const index = new Uint16Array((rings - 1) * BOUNDARY * 6);
  let k = 0;
  for (let r = 0; r < rings - 1; r++) {
    for (let i = 0; i < BOUNDARY; i++) {
      const j = (i + 1) % BOUNDARY;
      const a = r * BOUNDARY + i;
      const b = r * BOUNDARY + j;
      const c = (r + 1) * BOUNDARY + i;
      const d = (r + 1) * BOUNDARY + j;
      index[k++] = a; index[k++] = c; index[k++] = b;
      index[k++] = b; index[k++] = c; index[k++] = d;
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('aRing', new THREE.BufferAttribute(ringAttr, 1));
  geometry.setIndex(new THREE.BufferAttribute(index, 1));

  const uniforms: Record<string, THREE.IUniform> = {
    uColor: { value: new THREE.Vector3(...config.colour) },
    uGain: { value: config.gain },
    uPulse: { value: config.pulse ? 1 : 0 },
    uTime: { value: 0 },
    uFill: { value: 1 },
  };
  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    // The mark answers a click, so the ground it answers about cannot be
    // allowed to hide it - the same call `hoverHalo.ts` makes for the same
    // reason. It still FOLLOWS the ground; it is only never occluded by it.
    depthTest: false,
    depthWrite: false,
    side: THREE.DoubleSide,
    toneMapped: false,
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'atlas-subplot-mark';
  mesh.renderOrder = 28;
  mesh.frustumCulled = false;
  mesh.visible = false;

  const _p = new THREE.Vector3();
  const _o = new THREE.Vector3();
  let cell: SubPlotMarkCell | null = null;
  let built = false;
  let wanted = true;
  let mode: SubPlotMarkMode = 'fill';

  function rebuild(): void {
    built = false;
    if (!cell) return;
    const origin = config.groundAt(cell.x, cell.z, _o);
    if (!origin) return;
    mesh.position.copy(origin);
    for (let r = 0; r < rings; r++) {
      const s = RINGS[r]! * cell.radius_m;
      for (let i = 0; i < BOUNDARY; i++) {
        const dx = unit[i * 2]! * s;
        const dz = unit[i * 2 + 1]! * s;
        const at = config.groundAt(cell.x + dx, cell.z + dz, _p) ?? _o;
        const v = (r * BOUNDARY + i) * 3;
        positions[v] = at.x - origin.x;
        positions[v + 1] = at.y - origin.y;
        positions[v + 2] = at.z - origin.z;
      }
    }
    geometry.getAttribute('position').needsUpdate = true;
    geometry.computeBoundingSphere();
    built = true;
  }

  function sync(): void {
    mesh.visible = wanted && built && cell !== null && mode !== 'off';
  }

  return {
    object: mesh,
    get cellId(): string | null { return cell?.id ?? null; },
    get drawn(): boolean { return mesh.visible; },
    set(next: SubPlotMarkCell | null): boolean {
      const was = cell?.id ?? null;
      const drawnWas = mesh.visible;
      cell = next;
      rebuild();
      sync();
      return was !== (next?.id ?? null) || drawnWas !== mesh.visible;
    },
    refresh(): void {
      rebuild();
      sync();
    },
    setVisible(on: boolean): void {
      wanted = on;
      sync();
    },
    setMode(next: SubPlotMarkMode): void {
      mode = next;
      uniforms['uFill']!.value = next === 'fill' ? 1 : 0;
      sync();
    },
    update(dtSeconds: number): void {
      if (!mesh.visible) return;
      uniforms['uTime']!.value = (uniforms['uTime']!.value as number) + dtSeconds;
    },
    dispose(): void {
      geometry.dispose();
      material.dispose();
    },
  };
}
